import { IStudioOptions } from './interface';
import { UiMenu } from '../Ui/uiMenu';
import { UiLoadingScreen } from '../Ui/uiLoadingScreen';
import { UiPointer } from '../Ui/uiPointer';
import { UiArQrCode } from '../Ui/uiArQrCode';
import { EngineConfiguration } from './engineConfiguration';
import { LoaderEvent } from '../Tool/toolLoader';
import { jsonConverter } from '../Tool/Json';
import { getDevice, getQueryParam } from '../../Utils/html';
import { UiDragAnimation } from '../Ui/uiDragAnimation';
import { UiCameraRotate } from '../Ui/uiCameraRotate';
import { UiArrow } from '../Ui/uiArrows';
import '../Ui/ui.sass';

export class Engine extends EngineConfiguration {
    private uiLoadingScreen: UiLoadingScreen;

    private uiMenu: UiMenu;

    private uiPointer: UiPointer
    private uiArQrCode: UiArQrCode;

    constructor(studioOptions: IStudioOptions, callback?: (viewer: Engine) => void) {
        super(studioOptions);
        jsonConverter.clean(studioOptions)

        this.uiLoadingScreen = new UiLoadingScreen(this.container);
        this.uiLoadingScreen.show('Loading');

        this.uiMenu = new UiMenu(this.sceneContainer);
        this.uiMenu.onARRequested = () => {
            this.launchArProject();
        };

        this.uiPointer = new UiPointer(this.sceneContainer);
        this.uiArQrCode = new UiArQrCode(this.container);

        if (studioOptions.contents) {
            this.launchProject(studioOptions, () => {
                this.addUiWithSystem(studioOptions);
                if (callback) callback(this);
            });
        } else if (callback) callback(this);
    }

    private addUiWithSystem(studioOptions: IStudioOptions) {
        if (studioOptions.dragAnimation) {
            const dragAnim = new UiDragAnimation(
                this.system,
                this.sceneContainer,
                this.sceneryCamera
            );
            dragAnim.start = true;
        }
        if (studioOptions.cameraRotate) {
            const camRotate = new UiCameraRotate(this.system);
            camRotate.start = true;
        }
        if (this.system.isOnMobile) {
            new UiArrow(this.sceneContainer, this.mouseCatcher);
        }
    }

    // For Virtual Try-On for instance
    private _arLink: string

    public set arLink(v: string) {
        this._arLink = v;
    }

    private launchArProject() {
        const device = getDevice();
        if (this._arLink) {
            if (device === 'Computer') this.uiArQrCode.setQRCodeUrl(this._arLink);
            else this.openARLink(this._arLink, true, false);
        } else if (device === 'iOS') {
            this.launchIosARProject();
        } else if (device === 'Androïd') {
            this.launchAndroidARProject();
        } else {
            this.uiArQrCode.setQRCoreModelLink(this.projectId, this._modelLink);
        }
    }

    public getArQrCodeCanvas(): HTMLCanvasElement {
        return this.uiArQrCode.getQRCodeCanvas(this.projectId, this._modelLink);
    }

    private normalProjectStarted = false

    private launchNormalProject(studioOptions, callback?: (viewer: Engine) => void) {
        this.buildProject(studioOptions);
        if (studioOptions.configuration) {
            this.buildConfig();
            this.configurationTranslation.setTranslations([{ key: 'title', en: studioOptions.name }]);
        }
        this.uiMenu.setContentsManager(this.contentsManager);
        this.checkUiOptions(studioOptions);

        // In case there is nothing to load
        if (this.system.loader.checkFinished()) {
            this.completeLoad(callback);
        } else {
            this.system.loader.once(LoaderEvent.Finish, () => {
                this.completeLoad(callback);
            });
        }

        this.system.loader.on(LoaderEvent.Progress, (loaderData) => {
            const remainingCount = loaderData.remaining;
            const totalCount = loaderData.total;
            const progress = 1 - (remainingCount / totalCount);
            this.uiLoadingScreen.setProgress(progress);
        });
    }

    private completeLoad(callback?: (viewer: Engine) => void) {
        this.sceneryEvironment.resetShadowGroundHeight();
        this.startRendering(() => {
            this.uiLoadingScreen.success();
            this.uiMenu.start();
            this.showConfiguration();
            this.normalProjectStarted = true;
            if (callback) callback(this);
        });
    }

    private checkUiOptions(options: IStudioOptions) {
        this.uiPointer.checkOptions(options);
        this.uiMenu.checkOptions(options);
        this.uiLoadingScreen.checkOptions(options);
    }

    protected launchProject(studioOptions: IStudioOptions, callback?: () => void): void {
        const device = getDevice();
        this.checkModelLink(studioOptions);
        const ARFromQuery = getQueryParam('AR') === 'true';
        const isMobile = (device === 'iOS' || device === 'Androïd');
        const ARNeeded = ((ARFromQuery || studioOptions.ARfirst) && isMobile);
        if (ARNeeded) {
            this.launchArProject();
        } else {
            this.launchNormalProject(studioOptions, callback);
        }
    }

    private openARLink = (url: string, onFocus = true, ar = true) => {
        const link = document.createElement('a');
        if (ar) link.setAttribute('rel', 'ar');
        link.appendChild(document.createElement('img'));
        link.setAttribute('href', url);
        if (onFocus) {
            let focussed = false;
            window.addEventListener('focus', () => {
                if (!focussed) {
                    focussed = true;
                    if (!this.normalProjectStarted) this.launchNormalProject(this.options);
                }
            });
        }
        link.click();
    }

    private launchAndroidARProject() {
        const { glb } = this._modelLink;
        if (glb) {
            const intentUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${glb}&mode=ar_only#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=https://developers.google.com/ar;end;`;
            this.openARLink(intentUrl, false);
        }
    }

    private launchIosARProject() {
        const { usdz } = this._modelLink;
        if (usdz) {
            this.openARLink(usdz, true);
        }
    }
}

window.NakerStudioEngine = Engine;
